/**
 * @file http请求控制
 * @author mengchen <mengchen@babytree-inc.com>
 */
import axios from 'axios';
import queryString from './query-string';
import {
  getGOHost,
  getNewMapHost,
  getPHPHost,
  getMapiWebHost,
  getABTestingHost,
  getOpenHost,
  getWebviewHost,
  getMeitunHost
} from './config';
import { Loading } from './ui';
import Toast from 'antd-mobile/lib/toast';
import 'antd-mobile/lib/toast/style/index.css';

const requestLoading = {
  isLoading: false,
  cacheList: [],
  start: function (cache, message) {
    if (this.cacheList.length === 0 && this.isLoading === false) {
      this.isLoading = true;
      Loading.open({
        message
      });
    }
    this.cacheList.push(cache);
  },
  end: function (cache) {
    const index = this.cacheList.indexOf(cache);
    if (index > -1) {
      this.cacheList.splice(index, 1);
    }
    if (this.cacheList.length === 0 && this.isLoading === true) {
      Loading.close();
      this.isLoading = false;
    }
  }
};

const instance = axios.create({
  withCredentials: true,
  timeout: 10000,
  headers: {
    Accept: '*/*'
  }
});

const requestLocker = {};

const defaultOption = {
  toast: true,
  service: 'go',
  loading: true,
  loadingMessage: '',
  unkonwErrorRetry: 1
};

export const getBaseURL = function (service, serviceNum) {
  if (service.toLocaleLowerCase() === 'go') {
    return getGOHost(serviceNum);
  }
  if (service.toLocaleLowerCase() === 'php') {
    return getPHPHost(serviceNum);
  }
  if (service.toLocaleLowerCase() === 'newmapi') {
    return getNewMapHost();
  }
  if (service.toLocaleLowerCase() === 'mapiweb') {
    return getMapiWebHost();
  }
  if (service.toLocaleLowerCase() === 'meitun') {
    return getMeitunHost();
  }
  if (service.toLocaleLowerCase() === 'abtesting') {
    return getABTestingHost();
  }
  if (service.toLocaleLowerCase() === 'open') {
    return getOpenHost();
  }
  if (service.toLocaleLowerCase() === 'webview') {
    return getWebviewHost();
  }
  return '';
};

const requestfactory = function (url, axiosOptions = {}, options = {}) {
  if (options.key && requestLocker[`${options.key}`]) {
    const error = new Error(`${options.key} key is working`);
    return Promise.reject(error);
  }
  options = {
    ...defaultOption,
    ...options
  };
  options.key && (requestLocker[`${options.key}`] = true);
  const cache = +new Date();
  options.loading && requestLoading.start(cache, options.loadingMessage);
  axiosOptions.baseURL = getBaseURL(options.service, options.serviceNum);
  axiosOptions.url = url || axiosOptions.url;
  axiosOptions.params = axiosOptions.params || {};
  // axiosOptions.params.cache = cache;

  return instance
    .request(axiosOptions)
    .then((response = {}) => {
      options.key && (requestLocker[`${options.key}`] = false);
      options.loading && requestLoading.end(cache);
      const { status, data = {} } = response;
      if ((status == 200 && data.status == 'success') || data.rtn_code == '0') {
        return data;
      }
      let message = '';
      if (data.status == 'nonLogin') {
        message = '请登录后重试';
      }
      const error = new Error(
        message ||
        data.message ||
        data.rtn_msg ||
        data.msg ||
        '系统繁忙请稍后重试'
      );
      error.status = data.status || data.rtn_code;
      error.data = data.data;
      error.response = response;
      error.isApiError = true;
      throw error;
    })
    .catch((error) => {
      options.key && delete requestLocker[`${options.key}`];
      options.loading && requestLoading.end(cache);
      const message = error.message && error.message.toLowerCase();
      if (message.indexOf('network error') > -1) {
        const { unkonwErrorRetry } = options;
        if (unkonwErrorRetry > 0) {
          return requestfactory(url, axiosOptions, {
            ...options,
            unkonwErrorRetry: unkonwErrorRetry - 1
          });
        }
      }
      if (message.indexOf('timeout') > -1) {
        error.isTimeout = true;
        error.message = '请求超时，请稍后重试';
      }
      options.toast && Toast.info(error.message, 2, null, false);
      throw error;
    });
};

const serialize = function (obj = {}) {
  const str = [];
  for (const p in obj) {
    if (obj.hasOwnProperty(p)) {
      str.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
    }
  }
  return str.join('&');
};

export const request = {
  get: function ({ url, data = {}, axiosOptions = {}, options = {} }) {
    return requestfactory(
      url,
      {
        ...axiosOptions,
        method: 'get',
        params: data
      },
      options
    );
  },
  post: function ({ url, data = {}, axiosOptions = {}, options = {} }) {
    axiosOptions.headers = axiosOptions.headers || {};
    axiosOptions.headers['Content-Type'] = 'application/x-www-form-urlencoded';
    return requestfactory(
      url,
      {
        ...axiosOptions,
        method: 'post',
        data: queryString.stringify(data)
      },
      options
    );
  },
  postJson: function ({ url, data = {}, axiosOptions = {}, options = {} }) {
    axiosOptions.headers = axiosOptions.headers || {};
    axiosOptions.headers['Content-Type'] = 'application/json';
    return requestfactory(
      url,
      {
        ...axiosOptions,
        method: 'post',
        data
      },
      options
    );
  },
  postForm: function ({ url, data = {}, axiosOptions = {}, options = {} }) {
    axiosOptions.headers = axiosOptions.headers || {};
    axiosOptions.headers['Content-Type'] = 'form-data';
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });
    return requestfactory(
      url,
      {
        ...axiosOptions,
        method: 'post',
        data: formData
      },
      options
    );
  },
  request: function ({ url, data, axiosOptions = {}, options = {} }) {
    const method = axiosOptions.method || 'get';
    if (method === 'get') {
      axiosOptions.params = data;
    } else {
      axiosOptions.data = data;
    }
    return requestfactory(
      url,
      {
        ...axiosOptions,
        method
      },
      options
    );
  }
};

export const getMapiHeaders = function (data) {
  const queryObject = queryString.parseUrl(window.location.search);
  const { query } = queryObject;
  return {
    birthday: query['bpreg-brithday'] || '1900-01-01',
    platform: 1,
    clientInfo: JSON.stringify({
      clientSystem: /Android/.test(window.navigator.userAgent)
        ? 'Android'
        : 'IOS',
      clientAppVersion: query['babytree-app-version'],
      clientVersion: query['babytree-app-version'],
      latitude: query['babytree-app-latitude'],
      longitude: query['babytree-app-longitude'],
      deviceCode: query['android-id'] || query['babytree-app-mac'],
      screenwidth: window.screen.width,
      screenheight: window.screen.height,
      partner: 'babytree'
    }),
    protocol: document.location.protocol === 'https:' ? 'https' : 'http',
    signature: 'ddd',
    timestamp: Math.floor(+new Date() / 1000),
    ...data
  };
};
